import { useMemo } from 'react';
import { StepsType } from 'core/stepper/Stepper.types';
import { useGetSteps } from 'components/Stepper/useGetSteps';
import PersonalDetails from '../pages/PersonalDetails/PersonalDetails';
import { InvestorProfilesContainer } from '../pages/InvestorProfile/InvestorProfilesContainer';
import StrategySelectionPage from '../pages/StrategySelection/StrategySelectionPage';
import WithdrawalPlan from '../pages/WithdrawalPlan/WithdrawalPlan';
import ChangeOverview from '../pages/ChangeOverview/ChangeOverview';
import { useInvestorProfileChangePlan } from './useInvestorProfileChangePlan';
import { useChangePlan } from '../context';
import { useWithdrawalPlanChangePlan } from './useWithdrawalPlanChangePlan';
import useCommonAllocationDataProvider from '../../../context/useCommonAllocationDataProvider';

export const stepsKeys = {
    PersonalDetails: 'personal-details',
    InvestorProfilesContainer: 'investor-profile',
    StrategySelectionPage: 'strategy-selection',
    WithdrawalPlan: 'withdrawal-plan',
    ChangeOverview: 'change-overview',
};

export const process = {
    main: 'parent',
};

const useChangeExistingPlanFlow = () => {
    const { setupWithdrawalPlan } = useCommonAllocationDataProvider();

    const changeExistingPlanFlow: StepsType = useMemo(() => ({
        [stepsKeys.PersonalDetails]: {
            datasource: useChangePlan,
            process: process.main,
            stepperTitle: 'portfolio.changeStrategy.personalDetails',
            contentTitle: 'portfolio.changeStrategy.personalDetails',
            contentClassName: '',
            component: PersonalDetails,
            url: 'personal-details',
            next: [
                {
                    target: stepsKeys.InvestorProfilesContainer,
                    condition: () => true,
                },
            ],
            isBegin: true,
        },
        [stepsKeys.InvestorProfilesContainer]: {
            datasource: useInvestorProfileChangePlan,
            process: process.main,
            stepperTitle: 'portfolio.changeStrategy.investorProfile',
            contentTitle: 'portfolio.changeStrategy.investorProfile',
            contentClassName: '',
            component: InvestorProfilesContainer,
            url: 'investor-profile',
            prev: [
                {
                    target: stepsKeys.PersonalDetails,
                    condition: () => true,
                },
            ],
            next: [
                {
                    target: stepsKeys.StrategySelectionPage,
                    condition: () => true,
                },
            ],
        },
        [stepsKeys.StrategySelectionPage]: {
            datasource: useChangePlan,
            process: process.main,
            stepperTitle: 'portfolio.changeStrategy.strategy',
            contentTitle: 'portfolio.changeStrategy.strategy',
            contentClassName: '',
            component: StrategySelectionPage,
            url: 'strategy-selection',
            prev: [
                {
                    target: stepsKeys.InvestorProfilesContainer,
                    condition: () => true,
                },
            ],
            next: [
                {
                    target: stepsKeys.WithdrawalPlan,
                    condition: () => setupWithdrawalPlan,
                },
                {
                    target: stepsKeys.ChangeOverview,
                    condition: () => !setupWithdrawalPlan,
                },
            ],
        },
        [stepsKeys.WithdrawalPlan]: {
            datasource: useWithdrawalPlanChangePlan,
            process: process.main,
            stepperTitle: 'portfolio.createNewPortfolio.withdrawalPlan',
            contentTitle: 'portfolio.createNewPortfolio.withdrawalPlan',
            contentClassName: '',
            component: WithdrawalPlan,
            url: 'withdrawal-plan',
            prev: [
                {
                    target: stepsKeys.StrategySelectionPage,
                    condition: () => true,
                },
            ],
            next: [
                {
                    target: stepsKeys.ChangeOverview,
                    condition: () => true,
                },
            ],
        },
        [stepsKeys.ChangeOverview]: {
            datasource: useChangePlan,
            process: process.main,
            stepperTitle: 'portfolio.changeStrategy.overview',
            contentTitle: 'portfolio.changeStrategy.overview',
            contentClassName: '',
            component: ChangeOverview,
            url: 'change-overview',
            prev: [
                {
                    target: stepsKeys.WithdrawalPlan,
                    condition: () => setupWithdrawalPlan,
                },
                {
                    target: stepsKeys.StrategySelectionPage,
                    condition: () => !setupWithdrawalPlan,
                },
            ],
            isEnd: true,
        },
    }), [setupWithdrawalPlan]);

    const { stepsBasedOnState } = useGetSteps({ flow: changeExistingPlanFlow, initialStep: stepsKeys.PersonalDetails });

    return { stepsBasedOnState };
};

export default useChangeExistingPlanFlow;
